import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ChevronLeft, ChevronRight, RotateCw, ZoomIn, ZoomOut } from "lucide-react";
import { useResultReview } from "./primitives";

/** PDF a sinistra: pagina renderizzata dal backend + overlay bbox delle evidenze reali di quella pagina. */
export const ResultPdfPane = ({ jobId, page, setPage, pageCount, setPageCount, missingPages = [] }) => {
  const { evidenceBySegment, selected, select } = useResultReview();
  const [imgUrl, setImgUrl] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [rot, setRot] = useState(0);
  const [showOverlays, setShowOverlays] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let url;
    setErr(null);
    api.get(`/reader/jobs/${jobId}/page-image?page=${page}&dpi=140`, { responseType: "blob" })
      .then((r) => { setPageCount(parseInt(r.headers["x-page-count"] || "1", 10)); url = URL.createObjectURL(r.data); setImgUrl(url); })
      .catch(() => { setImgUrl(null); setErr("PDF non disponibile"); });
    return () => url && URL.revokeObjectURL(url);
  }, [jobId, page, setPageCount]);

  const overlays = Object.entries(evidenceBySegment).flatMap(([segId, list]) =>
    (list || []).filter((e) => e.page === page && e.bbox_pct).map((e) => ({ segId, ...e })));

  return (
    <div className="border border-border bg-white flex flex-col flex-1 min-h-0" data-testid="result-pdf-pane">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border p-1.5 text-xs">
        <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => setZoom((z) => Math.max(50, z - 25))} data-testid="result-pdf-zoom-out"><ZoomOut size={12} /></Button>
        <span className="font-mono w-10 text-center">{zoom}%</span>
        <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => setZoom((z) => Math.min(300, z + 25))} data-testid="result-pdf-zoom-in"><ZoomIn size={12} /></Button>
        <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => setRot((r) => (r + 90) % 360)} data-testid="result-pdf-rotate"><RotateCw size={12} /></Button>
        <Button size="sm" variant="outline" className="h-6 px-1.5 ml-2" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="result-pdf-prev"><ChevronLeft size={12} /></Button>
        <span className="font-bold" data-testid="result-pdf-page-label">Pagina {page} / {pageCount}</span>
        <Button size="sm" variant="outline" className="h-6 px-1.5" disabled={page >= pageCount} onClick={() => setPage(page + 1)} data-testid="result-pdf-next"><ChevronRight size={12} /></Button>
        {missingPages.length > 0 && (
          <span className="px-1 border border-red-400 bg-red-50 text-red-900 text-[9px] font-bold" data-testid="result-pdf-missing-badge">
            manca p.{missingPages.join(", ")} (mai ricevuta)
          </span>
        )}
        <label className="ml-auto flex items-center gap-1.5 text-[10px]">
          <Switch checked={showOverlays} onCheckedChange={setShowOverlays} data-testid="result-pdf-overlay-toggle" />
          Evidenze su questa pagina ({overlays.length})
        </label>
      </div>
      <div className="overflow-auto flex-1 relative bg-muted/30">
        <div className="relative origin-top-left" style={{ width: `${zoom}%`, transform: `rotate(${rot}deg)` }}>
          {imgUrl ? <img src={imgUrl} alt={`Pagina ${page}`} className="w-full block" data-testid="result-pdf-image" />
            : <div className="p-8 text-xs text-muted-foreground" data-testid="result-pdf-error">{err || "Caricamento pagina…"}</div>}
          {showOverlays && imgUrl && overlays.map((e) => {
            const b = e.bbox_pct;
            const sel = selected && selected.segId === e.segId && selected.evId === e.evidence_id;
            return (
              <div key={`${e.segId}-${e.evidence_id}`} title={`${e.segId}/${e.evidence_id}: ${e.text}`}
                onClick={() => select(e.segId, e.evidence_id)}
                className={`absolute cursor-pointer border-2 transition-colors ${sel ? "border-red-500 bg-red-500/20 z-10" : "border-cyan-500/70 bg-cyan-400/10 hover:bg-cyan-400/25"}`}
                style={{ left: `${b.x0}%`, top: `${b.y0}%`, width: `${Math.max(0.3, b.x1 - b.x0)}%`, height: `${Math.max(0.3, b.y1 - b.y0)}%` }}
                data-testid={`result-overlay-${e.segId}-${e.evidence_id}`}>
                <span className="absolute -top-3.5 left-0 bg-white/90 px-0.5 text-[8px] font-mono leading-3 border border-current">{e.segId}/{e.evidence_id}</span>
              </div>
            );
          })}
        </div>
      </div>
      {selected && (() => {
        const ev = (evidenceBySegment[selected.segId] || []).find((x) => x.evidence_id === selected.evId);
        return ev ? (
          <div className="border-t border-border px-2 py-1 text-[10px] bg-red-50 text-red-950" data-testid="result-selected-evidence">
            <b>{selected.segId}/{ev.evidence_id}</b> · pagina {ev.page} · «{ev.text}»
          </div>
        ) : null;
      })()}
    </div>
  );
};
